import React from 'react';
import {
  AppData,
  CompRef,
  DocumentServicesObject,
} from '@wix/document-services-types';
import { IProcessManifestProps, PanelLoader } from '../lifecycle';
import type {
  EditorElement,
  InlineElement,
} from '@wix/component-protocol/schema';
import { IWixCLIIntegrationAPI } from '../types';
import {
  PanelStore,
  ManifestComponentExtensionType,
  AbstractManifestStore,
} from '../stores';
import {
  ClientSpecMapService,
  ManifestStructureService,
} from '../sharedServices';
import {
  createEditorPlatformManifestServicesError,
  EditorPlatformManifestServicesErrorCode,
} from '../errors';
import { ManifestServiceFlags } from '../ManifestServiceFlags';
import type {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

export abstract class AbstractComponentFlow<
  TManifestStore extends AbstractManifestStore,
> {
  abstract type: ManifestComponentExtensionType;

  protected readonly panelLoader: PanelLoader;
  protected readonly clientSpecMapService: ClientSpecMapService;
  protected readonly manifestStructureService: ManifestStructureService;
  protected readonly manifestServiceFlags: ManifestServiceFlags;

  constructor(
    protected readonly dm: DocumentServicesObject,
    protected readonly editorPointerAPI: EditorPointerServiceAPI,
    protected readonly manifestStore: TManifestStore,
    protected readonly panelStore: PanelStore,
    protected readonly wixCLIIntegrationAPI: IWixCLIIntegrationAPI,
    manifestServiceFlags: ManifestServiceFlags,
  ) {
    this.panelLoader = new PanelLoader();
    this.manifestServiceFlags = manifestServiceFlags;
    this.clientSpecMapService = new ClientSpecMapService(
      this.dm,
      this.editorPointerAPI,
    );
    this.manifestStructureService = new ManifestStructureService(
      this.editorPointerAPI,
    );
  }

  abstract loadManifest({
    appDataComponent,
  }: IProcessManifestProps): Promise<void>;

  abstract loadPanels({
    appDataComponent,
  }: IProcessManifestProps): Promise<void>;

  async invalidateCache(): Promise<void> {
    // No-op by default. Subclasses with caching should override.
  }

  async _loadPanels(manifest: EditorElement | InlineElement, appData: AppData) {
    const nativeActions = manifest?.actions ?? {};
    const customActions = manifest?.customActions ?? {};

    const panels: Record<
      string,
      (editorPointer?: EditorPointer<CompRef>) => React.ReactNode
    > = {};

    const tasks = Object.entries({
      ...nativeActions,
      ...customActions,
    }).map(async ([actionName, action]) => {
      const panelAction = action?.execution?.panel;

      if (!panelAction) {
        return;
      }

      const panelFactory = await this.panelLoader.load(panelAction, appData);

      if (!panelFactory) {
        return;
      }

      panels[actionName] = (editorPointer?: EditorPointer<CompRef>) => {
        const hostContext = this.wixCLIIntegrationAPI.getSDKHostContext({
          appDefinitionId: appData.appDefinitionId,
          editorPointers: editorPointer ? [editorPointer] : [],
        });

        const auth = this.wixCLIIntegrationAPI.getSDKAuth({
          appDefinitionId: appData.appDefinitionId,
        });

        const panel = panelFactory({
          host: hostContext.getHost(),
          auth,
        });

        // TODO: move to function
        let panelReactComponent: React.ReactNode | React.ReactElement;

        if (typeof panel === 'function') {
          panelReactComponent = React.createElement(
            panel as React.ComponentType<any>,
          );
        } else if (
          React.isValidElement(panel) || // Already a React Element
          Array.isArray(panel) // Array of nodes (e.g. fragment)
        ) {
          panelReactComponent = panel;
        } else {
          throw createEditorPlatformManifestServicesError(
            EditorPlatformManifestServicesErrorCode.LoadComponentPanelError,
            `Panel factory has returned an unexpected value`,
          );
        }

        return panelReactComponent;
      };
    });

    await Promise.all(tasks);

    return panels;
  }
}
